<template>
  <div class="dash-root">

    <!-- ── Header ─────────────────────────────────────────── -->
    <div class="dash-header">
      <div class="dash-header__inner">
        <div class="dash-header__brand">
          <span class="dash-header__icon"><i class="fas fa-comments"></i></span>
          <h1 class="dash-header__title">Messages</h1>
        </div>
        <div class="dash-header__actions">
          <button class="btn btn--primary" @click="openMessageDialog(true)">
            <i class="fas fa-crown"></i> Business
          </button>
          <button class="btn btn--soft" @click="openMessageDialog(false)">
            <i class="fas fa-users"></i> Simple
          </button>
          <button class="btn btn--topup" @click="goToTopupsPage">
            <i class="fas fa-user-tie"></i> Top‑ups
            <span v-if="topupUnreadCount > 0" class="badge badge--red">{{ topupUnreadCount > 99 ? '99+' : topupUnreadCount }}</span>
          </button>
          <button class="btn btn--whatsapp" @click="openWhatsAppDialog">
            <i class="fab fa-whatsapp"></i> WhatsApp
          </button>
        </div>
      </div>

      <!-- Stats strip -->
      <div class="stats-strip">
        <div class="stat-chip">
          <span class="stat-chip__dot stat-chip__dot--unread"></span>
          <span class="stat-chip__val">{{ unreadChats.length }}</span>
          <span class="stat-chip__lbl">Unread</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-chip">
          <span class="stat-chip__dot stat-chip__dot--recent"></span>
          <span class="stat-chip__val">{{ recentConversations.length }}</span>
          <span class="stat-chip__lbl">Conversations</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-chip">
          <span class="stat-chip__dot stat-chip__dot--total"></span>
          <span class="stat-chip__val">{{ users.length }}</span>
          <span class="stat-chip__lbl">Active Clients</span>
        </div>
      </div>
    </div>

    <!-- ── Three columns ──────────────────────────────────── -->
    <div class="dash-grid">

      <!-- Left: Recent conversations -->
      <div class="panel">
        <div class="panel__head">
          <span class="panel__head-title">
            <i class="fas fa-history panel__head-icon"></i> Recent Conversations
          </span>
          <button class="icon-btn" @click="fetchRecentConversations" title="Refresh"><i class="fas fa-sync-alt"></i></button>
        </div>
        <div class="panel__body">
          <div v-if="recentConversations.length === 0" class="empty">
            <i class="fas fa-comments"></i><p>No conversations</p><small>Start a chat with a client!</small>
          </div>
          <div v-for="conv in recentConversations" :key="conv.conversation_id" class="row-item" :class="{ 'row-item--unread': conv.unread_count > 0 }" @click="goToChat(conv.user_id)">
            <div class="row-item__avatar"><img :src="image_avatar" alt="avatar" /><span class="dot dot--online"></span></div>
            <div class="row-item__body">
              <div class="row-item__top"><span class="row-item__name">{{ conv.name }}</span><span class="row-item__time">{{ formatTime(conv.last_message_time) }}</span></div>
              <div class="row-item__preview">{{ truncateMessage(conv.last_message) }}</div>
              <div class="row-item__meta">
                <span class="tag"><i :class="getMessageIcon(conv.last_message_type)"></i> {{ getMessageTypeLabel(conv.last_message_type) }}</span>
                <span v-if="conv.unread_count > 0" class="badge">{{ conv.unread_count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Middle: Pending messages -->
      <div class="panel">
        <div class="panel__head">
          <span class="panel__head-title"><i class="fas fa-inbox panel__head-icon"></i> Pending</span>
          <span v-if="unreadChats.length > 0" class="pill pill--alert">{{ unreadChats.length }}</span>
          <button class="icon-btn" @click="fetchUnreadChats" title="Refresh"><i class="fas fa-sync-alt"></i></button>
        </div>
        <div class="panel__body">
          <div v-if="unreadChats.length === 0" class="empty">
            <i class="fas fa-check-circle"></i><p>All caught up</p><small>No pending messages!</small>
          </div>
          <div v-for="chat in unreadChats" :key="chat.user_id" class="row-item" @click="goToChat(chat.user_id)">
            <div class="row-item__avatar"><img :src="image_avatar" alt="avatar" /><span class="dot dot--online"></span></div>
            <div class="row-item__body">
              <div class="row-item__top"><span class="row-item__name">{{ chat.name }}</span><span class="row-item__time">{{ formatTime(chat.last_message_time) }}</span></div>
              <div class="row-item__preview">{{ truncateMessage(chat.last_message) }}</div>
              <div class="row-item__meta"><span class="tag"><i class="fas fa-user"></i> Client</span><span v-if="chat.unread > 0" class="badge">{{ chat.unread }}</span></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Search + client list -->
      <div class="panel panel--search">
        <div class="panel__head"><span class="panel__head-title"><i class="fas fa-user-friends panel__head-icon"></i> Clients</span></div>
        <div class="search-wrap">
          <i class="fas fa-search search-wrap__icon"></i>
          <input v-model="search" type="text" placeholder="Search client…" class="search-wrap__input" />
          <button v-if="search" class="icon-btn icon-btn--sm" @click="clearSearch"><i class="fas fa-times"></i></button>
        </div>
        <div class="panel__body">
          <div v-if="users.length === 0 && search" class="empty"><i class="fas fa-user-slash"></i><p>No results</p><small>Try another keyword</small></div>
          <div v-for="user in users" :key="user.id" class="row-item" @click="goToChat(user.id)">
            <div class="row-item__avatar"><img :src="image_avatar" alt="avatar" /><span class="dot dot--online"></span></div>
            <div class="row-item__body"><div class="row-item__name">{{ user.username }}</div><div class="row-item__status"><span class="dot dot--online dot--inline"></span> Online</div></div>
            <button class="icon-btn" title="Open chat"><i class="fas fa-comment"></i></button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Group message modal ── -->
    <transition name="fade">
      <div v-if="showOverlay" class="modal-backdrop" @click.self="closeOverlay">
        <div class="modal" ref="messageBox">
          <div class="modal__head">
            <div class="modal__head-label">
              <span class="modal__head-icon"><i :class="businessFlag ? 'fas fa-crown' : 'fas fa-users'"></i></span>
              <div><div class="modal__head-title">Group message</div><div class="modal__head-sub">All {{ businessFlag ? 'Business' : 'Simple' }} clients</div></div>
            </div>
            <button class="icon-btn" @click="closeOverlay"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal__body">
            <div v-if="selectedFile" class="file-chip">
              <div v-if="filePreview.type === 'image'" class="file-chip__thumb"><img :src="filePreview.url" alt="Preview" @error="onPreviewError" /></div>
              <div v-else-if="filePreview.type === 'video'" class="file-chip__thumb"><video :src="filePreview.url" muted @error="onPreviewError"></video></div>
              <div v-else class="file-chip__icon"><i :class="getFileIcon(selectedFile.name)"></i></div>
              <div class="file-chip__info"><span class="file-chip__name">{{ selectedFile.name }}</span><span class="file-chip__size">{{ formatFileSize(selectedFile.size) }}</span></div>
              <button class="file-chip__remove" @click="removeFile"><i class="fas fa-times"></i></button>
            </div>
            <div class="compose-box">
              <div class="compose-box__attach">
                <button class="attach-btn" :class="{ active: showAttachmentMenu }" @click="showAttachmentMenu = !showAttachmentMenu"><i class="fas fa-paperclip"></i></button>
                <transition name="pop"><div v-if="showAttachmentMenu" class="attach-menu"><button class="attach-menu__item" @click="selectFile('image')"><i class="fas fa-image"></i> Photo</button><button class="attach-menu__item" @click="selectFile('video')"><i class="fas fa-video"></i> Video</button><button class="attach-menu__item" @click="selectFile('file')"><i class="fas fa-file"></i> Document</button></div></transition>
              </div>
              <textarea v-model="groupMessage" :placeholder="selectedFile ? 'Add a caption…' : 'Your message…'" class="compose-box__textarea" @input="messageTyped = (groupMessage.trim().length > 0 || !!selectedFile)"></textarea>
              <span class="compose-box__count">{{ groupMessage.length }}/500</span>
            </div>
          </div>
          <div class="modal__foot">
            <button class="btn btn--ghost" @click="closeOverlay">Cancel</button>
            <button class="btn btn--primary" :disabled="(!messageTyped && !selectedFile) || $store.state.isLoading" @click="sendGroupMessage"><i v-if="$store.state.isLoading" class="fas fa-spinner fa-spin"></i><i v-else class="fas fa-paper-plane"></i> Send</button>
          </div>
          <input ref="imageInput" type="file" accept="image/*" style="display:none" @change="handleFileSelect" />
          <input ref="videoInput" type="file" accept="video/*" style="display:none" @change="handleFileSelect" />
          <input ref="fileInput"  type="file" accept="*/*"    style="display:none" @change="handleFileSelect" />
        </div>
      </div>
    </transition>

    <!-- ── WhatsApp DIALOG ── -->
    <transition name="modal-pop">
      <div v-if="whatsappDialogVisible" class="modal-backdrop" @click.self="closeWhatsAppDialog">
        <div class="modal modal--whatsapp">

          <!-- Head -->
          <div class="modal__head">
            <div class="modal__head-label">
              <span class="modal__head-icon modal__head-icon--whatsapp"><i class="fab fa-whatsapp"></i></span>
              <div>
                <div class="modal__head-title">Share via WhatsApp</div>
                <div class="modal__head-sub">
                  {{ filteredWhatsappClients.length }} client{{ filteredWhatsappClients.length === 1 ? '' : 's' }} · single selection
                </div>
              </div>
            </div>

            <!-- ── Language toggle ── -->
            <div class="lang-toggle">
              <button
                class="lang-btn"
                :class="{ 'lang-btn--active': waLang === 'fr' }"
                @click="setWaLang('fr')"
              >🇫🇷 FR</button>
              <button
                class="lang-btn"
                :class="{ 'lang-btn--active': waLang === 'en' }"
                @click="setWaLang('en')"
              >🇬🇧 EN</button>
            </div>

            <button class="icon-btn" @click="closeWhatsAppDialog"><i class="fas fa-times"></i></button>
          </div>

          <!-- Body -->
          <div class="modal__body modal__body--whatsapp">

            <!-- Selected client summary -->
            <transition name="pop">
              <div v-if="selectedClient" class="wa-selected">
                <div class="wa-selected__avatar"><img :src="image_avatar" alt="avatar" /><span class="dot dot--online"></span></div>
                <div class="wa-selected__info">
                  <div class="wa-selected__name">{{ selectedClient.username }}</div>
                  <div class="wa-selected__phone" v-if="formattedSelectedPhone"><i class="fas fa-phone-alt"></i> {{ formattedSelectedPhone }}</div>
                  <div class="wa-selected__phone wa-selected__phone--missing" v-else><i class="fas fa-exclamation-triangle"></i> {{ waLang === 'fr' ? 'Aucun numéro renseigné' : 'No phone number provided' }}</div>
                </div>
                <button class="wa-selected__clear" @click="selectedClientId = null" title="Deselect"><i class="fas fa-times"></i></button>
              </div>
            </transition>

            <!-- Search -->
            <div class="search-wrap wa-search">
              <i class="fas fa-search search-wrap__icon"></i>
              <input v-model="whatsappSearch" type="text" :placeholder="waLang === 'fr' ? 'Rechercher un client…' : 'Search client…'" class="search-wrap__input" />
              <button v-if="whatsappSearch" class="icon-btn icon-btn--sm" @click="whatsappSearch = ''"><i class="fas fa-times"></i></button>
            </div>

            <!-- Client list -->
            <div class="wa-list">
              <transition-group name="wa-item" tag="div" class="wa-list__items">
                <div
                  v-for="client in filteredWhatsappClients" :key="client.id"
                  class="wa-client" :class="{ 'wa-client--active': selectedClientId === client.id }"
                  role="radio" :aria-checked="selectedClientId === client.id"
                  @click="selectClient(client.id)"
                >
                  <div class="wa-client__avatar"><img :src="image_avatar" alt="avatar" /><span class="dot dot--online"></span></div>
                  <div class="wa-client__body">
                    <div class="wa-client__name">{{ client.username }}</div>
                    <div class="wa-client__phone" v-if="client.phone_number"><i class="fab fa-whatsapp"></i> {{ client.phone_number }}</div>
                    <div class="wa-client__phone wa-client__phone--missing" v-else><i class="fas fa-exclamation-circle"></i> {{ waLang === 'fr' ? 'Pas de numéro' : 'No number' }}</div>
                  </div>
                  <div class="wa-client__check"><i class="fas fa-check"></i></div>
                </div>
              </transition-group>
              <div v-if="filteredWhatsappClients.length === 0" class="empty-small">
                <i class="fas fa-user-slash"></i><br />{{ waLang === 'fr' ? 'Aucun client trouvé' : 'No clients found' }}
              </div>
            </div>

            <!-- Quick templates -->
            <div class="wa-templates">
              <span class="wa-templates__label"><i class="fas fa-bolt"></i> {{ waLang === 'fr' ? 'Modèles rapides' : 'Quick templates' }}</span>
              <div class="wa-templates__row">
                <button
                  v-for="tpl in currentQuickTemplates" :key="tpl.label"
                  class="wa-templates__chip"
                  @click="whatsappMessageTemplate = tpl.text"
                >{{ tpl.label }}</button>
              </div>
            </div>

            <!-- Message -->
            <div class="form-group">
              <label class="wa-label">{{ waLang === 'fr' ? 'Message' : 'Message' }}</label>
              <textarea v-model="whatsappMessageTemplate" rows="6" maxlength="1000" class="compose-box__textarea wa-textarea"></textarea>
              <div class="wa-textarea-foot">
                <span class="template-hint">{name} → {{ waLang === 'fr' ? 'nom du client' : "client's name" }} · {link} → {{ waLang === 'fr' ? 'lien du chat' : 'chat link' }}</span>
                <span class="wa-char-count" :class="{ 'wa-char-count--warn': whatsappMessageTemplate.length > 900 }">{{ whatsappMessageTemplate.length }}/1000</span>
              </div>
            </div>

            <!-- Preview -->
            <div class="wa-preview" :class="{ 'wa-preview--empty': !selectedClientId }">
              <div class="wa-preview__head">
                <i class="fab fa-whatsapp"></i>
                {{ waLang === 'fr' ? 'Aperçu du message' : 'Message preview' }}
                <span v-if="selectedClient">{{ waLang === 'fr' ? 'pour' : 'for' }} {{ selectedClient.username }}</span>
              </div>
              <!-- Fake WhatsApp bubble -->
              <div class="wa-preview__phone-frame">
                <div class="wa-preview__bubble-wrap">
                  <div class="wa-preview__bubble">{{ messagePreview }}</div>
                  <span class="wa-preview__tick">{{ waLang === 'fr' ? 'Aperçu' : 'Preview' }} ✓✓</span>
                </div>
              </div>
            </div>

          </div>

          <!-- Foot -->
          <div class="modal__foot">
            <button class="btn btn--ghost" @click="closeWhatsAppDialog">{{ waLang === 'fr' ? 'Annuler' : 'Cancel' }}</button>
            <button class="btn btn--secondary" @click="copySingleLink" :disabled="!selectedClientId">
              <i :class="copied ? 'fas fa-check' : 'fas fa-copy'"></i>
              {{ copied ? (waLang === 'fr' ? 'Copié !' : 'Copied!') : (waLang === 'fr' ? 'Copier le lien' : 'Copy link') }}
            </button>
            <button class="btn btn--whatsapp" :class="{ 'btn--whatsapp-ready': selectedClientId }" @click="sendWhatsAppToSelected" :disabled="!selectedClientId">
              <i class="fab fa-whatsapp"></i> {{ waLang === 'fr' ? 'Envoyer' : 'Send' }}
            </button>
          </div>

        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from "axios";

const CHAT_LINK = 'https://myapp.pettycashglobal.com/?open_chat=true';

const DEFAULT_TEMPLATES = {
  fr: `Bonjour {name},

Vous pouvez échanger directement avec notre équipe via notre service de messagerie.

Pour démarrer une conversation, cliquez sur le lien suivant :
${CHAT_LINK}

Nous restons à votre disposition pour toute assistance.

Cordialement,
L'équipe PettyCash SAS`,

  en: `Hello {name},

You can connect directly with our team via our messaging service.

To start a conversation, click the following link:
${CHAT_LINK}

We remain available for any assistance.

Best regards,
The PettyCash SAS team`,
};

const QUICK_TEMPLATES = {
  fr: [
    {
      label: 'Discussion',
      text: `Bonjour {name},\n\nJ'aimerais échanger avec vous.\n\nRejoignez la conversation : ${CHAT_LINK}`,
    },
    {
      label: 'Rappel',
      text: `Bonjour {name},\n\nCeci est un rappel concernant votre compte.\n\nCliquez ici pour continuer : ${CHAT_LINK}`,
    },
    {
      label: 'Bienvenue',
      text: `Bonjour {name} 👋\n\nBienvenue ! N'hésitez pas à nous contacter à tout moment.\n\nDémarrez la conversation : ${CHAT_LINK}`,
    },
    {
      label: 'Suivi',
      text: `Bonjour {name},\n\nNous faisons suite à votre dernière demande. Avez-vous besoin d'aide ?\n\n${CHAT_LINK}`,
    },
  ],
  en: [
    {
      label: 'Discussion',
      text: `Hello {name},\n\nI would like to discuss something with you.\n\nJoin the conversation: ${CHAT_LINK}`,
    },
    {
      label: 'Reminder',
      text: `Hello {name},\n\nThis is a reminder regarding your account.\n\nClick here to continue: ${CHAT_LINK}`,
    },
    {
      label: 'Welcome',
      text: `Hello {name} 👋\n\nWelcome aboard! Feel free to message us anytime.\n\nStart the conversation: ${CHAT_LINK}`,
    },
    {
      label: 'Follow-up',
      text: `Hello {name},\n\nWe're following up on your last request, do you need any further help?\n\n${CHAT_LINK}`,
    },
  ],
};

export default {
  data() {
    return {
      unreadChats: [],
      users: [],
      recentConversations: [],
      search: "",
      groupMessage: "",
      showOverlay: false,
      messageTyped: false,
      businessFlag: true,
      image_avatar: require('@/assets/images/avatars/avatar.png'),
      selectedFile: null,
      filePreview: { type: null, url: null },
      showAttachmentMenu: false,
      previewError: false,
      topupUnreadCount: 0,

      // WhatsApp dialog
      whatsappDialogVisible: false,
      whatsappSearch: "",
      selectedClientId: null,
      copied: false,
      waLang: 'fr',
      whatsappMessageTemplate: DEFAULT_TEMPLATES.fr,
      defaultCountryCode: '237',
      baseUrl: `https://${CHAT_LINK}`,
    };
  },

  computed: {
    urgentCount() {
      return this.unreadChats.filter(chat => chat.unread > 3).length;
    },

    currentQuickTemplates() {
      return QUICK_TEMPLATES[this.waLang] || QUICK_TEMPLATES.fr;
    },

    filteredWhatsappClients() {
      if (!this.whatsappSearch.trim()) return this.users;
      const q = this.whatsappSearch.toLowerCase();
      return this.users.filter(u => u.username.toLowerCase().includes(q));
    },

    selectedClient() {
      return this.users.find(u => u.id === this.selectedClientId) || null;
    },

    formattedSelectedPhone() {
      if (!this.selectedClient || !this.selectedClient.phone_number) return '';
      return this.formatPhoneForWhatsApp(this.selectedClient.phone_number);
    },

    messagePreview() {
      if (!this.selectedClientId) {
        return this.waLang === 'fr'
          ? "Sélectionnez un client pour voir l'aperçu du message."
          : "Select a client to see the message preview.";
      }
      const client = this.selectedClient;
      if (!client) return "";
      return this.whatsappMessageTemplate
        .replace(/{name}/g, client.username)
        .replace(/{link}/g, CHAT_LINK);
    },
  },

  watch: {
    search(val) {
      if (val.trim() === "") this.fetchUsers();
      else this.fetchUsersSearch(val.trim());
    },
  },

  mounted() {
    this.fetchUnreadChats();
    this.fetchUsers();
    this.fetchRecentConversations();
    this.fetchTopupUnreadCount();
    document.addEventListener('keydown', this.handleEscape);
    this.refreshInterval = setInterval(() => {
      this.fetchUnreadChats();
      this.fetchRecentConversations();
      this.fetchTopupUnreadCount();
    }, 30000);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape);
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  },

  methods: {

    // ── Language switch ──────────────────────────────────────────────────
    setWaLang(lang) {
      this.waLang = lang;
      // Only reset template if it still matches one of the defaults
      // (don't overwrite if the user has customized it)
      const otherLang = lang === 'fr' ? 'en' : 'fr';
      if (
        this.whatsappMessageTemplate === DEFAULT_TEMPLATES[otherLang] ||
        this.whatsappMessageTemplate === DEFAULT_TEMPLATES[lang]
      ) {
        this.whatsappMessageTemplate = DEFAULT_TEMPLATES[lang];
      }
    },

    // ── Navigation ───────────────────────────────────────────────────────
    goToChat(userId) {
      this.$router.push({ name: 'chat.chat:id', params: { id: userId } });
    },
    goToTopupsPage() {
      this.$router.push({ name: 'manager.topups' });
    },

    // ── Recent conversations ─────────────────────────────────────────────
    async fetchRecentConversations() {
      try {
        const response = await axios.get('/api/v1/manager/recent-conversations/');
        if (response.data.success) this.recentConversations = response.data.conversations;
      } catch (error) { console.error('Error loading recent conversations:', error); }
    },

    async fetchTopupUnreadCount() {
      try {
        const res = await axios.get('/api/v1/manager/topup/unread/');
        this.topupUnreadCount = res.data.unread || 0;
      } catch (error) { console.error('Failed to fetch top-up unread count', error); }
    },

    handleEscape(event) {
      if (event.key === 'Escape' && this.showOverlay) this.closeOverlay();
    },

    // ── Group message modal ──────────────────────────────────────────────
    openMessageDialog(isBusiness) {
      this.groupMessage = "";
      this.messageTyped = false;
      this.showOverlay = true;
      this.businessFlag = isBusiness;
      this.removeFile();
      this.showAttachmentMenu = false;
    },
    closeOverlay() {
      this.showOverlay = false;
      this.groupMessage = "";
      this.messageTyped = false;
      this.removeFile();
      this.showAttachmentMenu = false;
    },
    clearSearch() { this.search = ""; },
    selectFile(type) {
      this.showAttachmentMenu = false;
      if (type === 'image') this.$refs.imageInput.click();
      else if (type === 'video') this.$refs.videoInput.click();
      else this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      const maxSize = file.type.startsWith('video/') ? 2000 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) { alert(`Max file size is ${maxSize / (1024 * 1024)} MB`); return; }
      this.selectedFile = file;
      this.previewError = false;
      this.createFilePreview(file);
      this.messageTyped = true;
      event.target.value = '';
    },
    createFilePreview(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreview.url = e.target.result;
        if (file.type.startsWith('image/')) this.filePreview.type = 'image';
        else if (file.type.startsWith('video/')) this.filePreview.type = 'video';
        else this.filePreview.type = 'file';
      };
      reader.readAsDataURL(file);
    },
    removeFile() {
      this.selectedFile = null;
      this.filePreview = { type: null, url: null };
      this.previewError = false;
      this.messageTyped = this.groupMessage.trim().length > 0;
    },
    getFileType(file) {
      if (file.type.startsWith('image/')) return 'image';
      if (file.type.startsWith('video/')) return 'video';
      return 'file';
    },
    getFileIcon(fileName) {
      if (!fileName) return 'fas fa-file';
      const ext = fileName.split('.').pop().toLowerCase();
      const map = { pdf: 'fas fa-file-pdf', doc: 'fas fa-file-word', docx: 'fas fa-file-word', xls: 'fas fa-file-excel', xlsx: 'fas fa-file-excel', ppt: 'fas fa-file-powerpoint', pptx: 'fas fa-file-powerpoint', txt: 'fas fa-file-alt', zip: 'fas fa-file-archive', rar: 'fas fa-file-archive', jpg: 'fas fa-file-image', jpeg: 'fas fa-file-image', png: 'fas fa-file-image', mp4: 'fas fa-file-video', mov: 'fas fa-file-video' };
      return map[ext] || 'fas fa-file';
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    },
    onPreviewError() { this.previewError = true; },

    async sendGroupMessage() {
      this.$store.commit("setIsLoading", true);
      try {
        const formData = new FormData();
        formData.append('business_account', this.businessFlag);
        if (this.selectedFile) {
          formData.append('file', this.selectedFile);
          formData.append('message_type', this.getFileType(this.selectedFile));
          if (this.groupMessage.trim()) formData.append('message', this.groupMessage.trim());
        } else {
          formData.append('message', this.groupMessage.trim());
          formData.append('message_type', 'text');
        }
        await axios.post("/api/v1/manager/message_business_or_simple_account/", formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        this.$store.commit('setSnackBarMessage', "Message sent!");
        this.$store.commit('setSnackBarColor', "green");
        this.$store.commit('activateSnackBar', true);
        this.closeOverlay();
      } catch (error) {
        const msg = error.response?.data?.error || "Failed to send message.";
        this.$store.commit('setSnackBarMessage', msg);
        this.$store.commit('setSnackBarColor', "red darken-4");
        this.$store.commit('activateSnackBar', true);
      }
      this.$store.commit("setIsLoading", false);
    },

    // ── Users ────────────────────────────────────────────────────────────
    async fetchUsers() {
      try {
        const response = await axios.post("/api/v1/manager/all_user/");
        this.users = response.data;
      } catch (error) { this.handleError(error); }
    },
    async fetchUsersSearch(client) {
      try {
        const response = await axios.get(`/api/v1/manager/get/clients/${client}/`);
        this.users = response.data;
      } catch (error) { this.handleError(error); }
    },
    async fetchUnreadChats() {
      try {
        const response = await axios.get('/api/v1/manager/get_unread_client_messages/');
        const data = response.data;
        if (Array.isArray(data.last_messages) && data.last_messages.length > 0) {
          this.unreadChats = data.last_messages.map(msg => ({
            name: msg.name?.trim() || "Unknown",
            user_id: msg.user_id,
            last_message: msg.last_message || "",
            last_message_time: msg.last_message_time || "",
            unread: typeof msg.unread === "number" ? msg.unread : 0,
          }));
        } else { this.unreadChats = []; }
      } catch (error) { console.error('Error loading unread chats:', error.message); }
    },

    truncateMessage(message) {
      if (!message) return "No message";
      return message.length > 55 ? message.substring(0, 55) + "…" : message;
    },
    formatTime(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const now = new Date();
      const diffHours = (now - date) / (1000 * 60 * 60);
      if (diffHours < 1) return "Just now";
      if (diffHours < 24) return Math.floor(diffHours) + "h";
      return Math.floor(diffHours / 24) + "d";
    },
    getMessageIcon(messageType) {
      const icons = { text: 'fas fa-comment', image: 'fas fa-image', video: 'fas fa-video', audio: 'fas fa-microphone', file: 'fas fa-file', sticker: 'fas fa-smile', location: 'fas fa-map-marker-alt', transfer: 'fas fa-dollar-sign' };
      return icons[messageType] || 'fas fa-comment';
    },
    getMessageTypeLabel(messageType) {
      const labels = { text: 'Message', image: 'Photo', video: 'Video', audio: 'Audio', file: 'File', sticker: 'Sticker', location: 'Location', transfer: 'Transfer' };
      return labels[messageType] || 'Message';
    },
    handleError(error) {
      const msg = error.response ? `${error.response.status} ${error.response.statusText}` : "Connection error.";
      this.$store.commit('setSnackBarMessage', msg);
      this.$store.commit('setSnackBarColor', "red darken-4");
      this.$store.commit('activateSnackBar', true);
    },

    // ── WhatsApp methods ─────────────────────────────────────────────────
    openWhatsAppDialog() {
      this.selectedClientId = null;
      this.whatsappSearch = "";
      this.copied = false;
      this.waLang = 'fr';
      this.whatsappMessageTemplate = DEFAULT_TEMPLATES.fr;
      this.whatsappDialogVisible = true;
    },
    closeWhatsAppDialog() {
      this.whatsappDialogVisible = false;
      this.selectedClientId = null;
      this.whatsappSearch = "";
      this.copied = false;
    },
    selectClient(id) {
      this.selectedClientId = (this.selectedClientId === id) ? null : id;
    },
    formatPhoneForWhatsApp(rawPhone) {
      if (!rawPhone) return '';
      let digits = rawPhone.toString().replace(/\D/g, '');
      if (digits.startsWith('0')) digits = digits.substring(1);
      if (digits.length === 9) {
        digits = this.defaultCountryCode + digits;
      } else if (digits.length === 12 && digits.startsWith(this.defaultCountryCode)) {
        // already has country code
      } else {
        console.warn(`[WhatsApp] Non-standard phone format (${digits.length} digits): "${digits}"`);
      }
      return digits ? `+${digits}` : '';
    },
    async sendWhatsAppToSelected() {
      if (!this.selectedClientId) {
        this.showSnackBar(this.waLang === 'fr' ? 'Sélectionnez un client d\'abord' : 'Please select a client first', 'red darken-4');
        return;
      }
      const client = this.users.find(u => u.id === this.selectedClientId);
      if (!client) {
        this.showSnackBar(this.waLang === 'fr' ? 'Client introuvable' : 'Client not found', 'red darken-4');
        return;
      }
      const internationalPhone = this.formatPhoneForWhatsApp(client.phone_number || '');
      const message = this.whatsappMessageTemplate
        .replace(/{name}/g, client.username)
        .replace(/{link}/g, CHAT_LINK);
      const encodedMessage = encodeURIComponent(message);
      let url;
      if (internationalPhone && internationalPhone.length > 5) {
        url = `https://wa.me/${encodeURIComponent(internationalPhone)}?text=${encodedMessage}`;
      } else {
        url = `https://wa.me/?text=${encodedMessage}`;
        this.showSnackBar(
          this.waLang === 'fr'
            ? `Pas de numéro valide pour ${client.username}, ouverture sans destinataire`
            : `No valid phone number for ${client.username}, opening without a recipient`,
          'orange'
        );
      }
      window.open(url, '_blank');
      this.showSnackBar(
        this.waLang === 'fr' ? `WhatsApp ouvert pour ${client.username}` : `WhatsApp opened for ${client.username}`,
        'green'
      );
      this.closeWhatsAppDialog();
    },
    async copySingleLink() {
      if (!this.selectedClientId) {
        this.showSnackBar(this.waLang === 'fr' ? 'Aucun client sélectionné' : 'No client selected', 'red darken-4');
        return;
      }
      const client = this.users.find(u => u.id === this.selectedClientId);
      if (!client) return;
      try {
        await navigator.clipboard.writeText(`https://${CHAT_LINK}`);
        this.copied = true;
        this.showSnackBar(
          this.waLang === 'fr' ? `Lien de chat pour ${client.username} copié !` : `Chat link for ${client.username} copied!`,
          'green'
        );
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (err) {
        this.showSnackBar(this.waLang === 'fr' ? 'Échec de la copie' : 'Failed to copy the link', 'red darken-4');
      }
    },
    showSnackBar(message, color) {
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', color);
      this.$store.commit('activateSnackBar', true);
    },
  },
};
</script>

<style scoped>
/* ─── Topup / WhatsApp buttons ─── */
.btn--topup { background: #f4a84a; color: white; box-shadow: 0 2px 8px rgba(244,168,74,0.3); }
.btn--topup:hover:not(:disabled) { opacity: .88; transform: translateY(-1px); }
.badge--red { background: #e57373; color: #fff; font-size: 11px; padding: 2px 8px; border-radius: 20px; margin-left: 6px; }
.btn--whatsapp { background: #25D366; color: white; box-shadow: 0 2px 8px rgba(37,211,102,0.3); }
.btn--whatsapp:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
.btn--whatsapp:disabled { opacity: .5; cursor: not-allowed; }

/* ─── Layout ─── */
.dash-root { min-height: 100vh; background: #f4f6fb; padding: 20px; box-sizing: border-box; font-family: 'Inter', system-ui, sans-serif; color: #2d3142; }
.dash-header { background: #ffffff; border-radius: 16px; box-shadow: 0 1px 4px rgba(44,51,73,0.07); margin-bottom: 20px; overflow: hidden; }
.dash-header__inner { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px; border-bottom: 1px solid #e9ecf5; }
.dash-header__brand { display: flex; align-items: center; gap: 10px; }
.dash-header__icon { width: 36px; height: 36px; background: #eef0fc; color: #5c6bc0; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
.dash-header__title { margin: 0; font-size: 20px; font-weight: 700; letter-spacing: -0.3px; color: #2d3142; }
.dash-header__actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
.stats-strip { display: flex; align-items: center; padding: 12px 24px; gap: 0; }
.stat-chip { display: flex; align-items: center; gap: 8px; padding: 0 20px 0 0; }
.stat-chip__dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.stat-chip__dot--unread { background: #5c6bc0; }
.stat-chip__dot--recent { background: #42b8a4; }
.stat-chip__dot--total { background: #f4a84a; }
.stat-chip__val { font-size: 18px; font-weight: 700; color: #2d3142; line-height: 1; }
.stat-chip__lbl { font-size: 12px; color: #6b7a99; font-weight: 500; }
.stat-divider { width: 1px; height: 24px; background: #e9ecf5; margin: 0 20px; }

/* ─── Buttons ─── */
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 20px; font-size: 13px; font-weight: 600; border: none; cursor: pointer; transition: opacity .15s, box-shadow .15s, transform .15s; white-space: nowrap; }
.btn--primary { background: #5c6bc0; color: #fff; box-shadow: 0 2px 8px rgba(92,107,192,0.3); }
.btn--primary:hover:not(:disabled) { opacity: .88; transform: translateY(-1px); }
.btn--primary:disabled { opacity: .5; cursor: not-allowed; }
.btn--soft { background: #eef0fc; color: #5c6bc0; }
.btn--soft:hover { background: #dde1f8; }
.btn--ghost { background: transparent; border: 1px solid #e9ecf5; color: #6b7a99; }
.btn--ghost:hover { background: #f4f6fb; }
.btn--secondary { background: #f4f6fb; color: #2d3142; border: 1px solid #e9ecf5; }
.btn--secondary:hover:not(:disabled) { background: #eef0fc; border-color: #dde1f8; }
.btn--secondary:disabled { opacity: .5; cursor: not-allowed; }
.icon-btn { width: 32px; height: 32px; border: none; background: transparent; color: #6b7a99; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: background .15s, color .15s; flex-shrink: 0; }
.icon-btn:hover { background: #eef0fc; color: #5c6bc0; }
.icon-btn--sm { width: 26px; height: 26px; }

/* ─── Grid / panels ─── */
.dash-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 18px; align-items: start; }
.panel { background: #ffffff; border-radius: 16px; box-shadow: 0 1px 4px rgba(44,51,73,0.07); display: flex; flex-direction: column; overflow: hidden; max-height: calc(100vh - 220px); }
.panel__head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px 12px; border-bottom: 1px solid #e9ecf5; flex-shrink: 0; }
.panel__head-title { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: #2d3142; }
.panel__head-icon { color: #5c6bc0; font-size: 14px; }
.panel__body { overflow-y: auto; padding: 10px; flex: 1; }
.panel__body::-webkit-scrollbar { width: 4px; }
.panel__body::-webkit-scrollbar-thumb { background: #e9ecf5; border-radius: 4px; }
.panel--search .search-wrap { padding: 10px 12px; border-bottom: 1px solid #e9ecf5; display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.search-wrap__icon { color: #9aa0ba; font-size: 13px; flex-shrink: 0; }
.search-wrap__input { flex: 1; border: none; outline: none; font-size: 13px; color: #2d3142; background: transparent; font-family: inherit; }
.search-wrap__input::placeholder { color: #9aa0ba; }

/* ─── Row items ─── */
.row-item { display: flex; align-items: center; gap: 10px; padding: 10px 8px; border-radius: 8px; cursor: pointer; transition: background .13s; position: relative; }
.row-item::before { content: ''; position: absolute; left: 0; top: 10px; bottom: 10px; width: 3px; border-radius: 3px; background: transparent; transition: background .13s; }
.row-item:hover { background: #eef0fc; }
.row-item:hover::before, .row-item--unread::before { background: #5c6bc0; }
.row-item + .row-item { margin-top: 2px; }
.row-item__avatar { position: relative; flex-shrink: 0; }
.row-item__avatar img { width: 38px; height: 38px; border-radius: 50%; object-fit: cover; display: block; }
.dot { position: absolute; bottom: 1px; right: 1px; width: 9px; height: 9px; border-radius: 50%; border: 2px solid #ffffff; }
.dot--online { background: #4db87a; }
.dot--offline { background: #9aa0ba; }
.dot--inline { position: static; display: inline-block; width: 7px; height: 7px; border: none; vertical-align: middle; margin-right: 2px; }
.row-item__body { flex: 1; min-width: 0; }
.row-item__top { display: flex; justify-content: space-between; align-items: baseline; gap: 4px; }
.row-item__name { font-size: 13px; font-weight: 600; color: #2d3142; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.row-item__time { font-size: 11px; color: #9aa0ba; white-space: nowrap; flex-shrink: 0; }
.row-item__preview { font-size: 12px; color: #6b7a99; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px; }
.row-item__meta { display: flex; align-items: center; justify-content: space-between; margin-top: 4px; }
.row-item__status { font-size: 11px; color: #9aa0ba; margin-top: 2px; }
.tag { font-size: 11px; color: #9aa0ba; display: flex; align-items: center; gap: 3px; }
.badge { background: #5c6bc0; color: #fff; font-size: 10px; font-weight: 700; padding: 1px 7px; border-radius: 10px; min-width: 18px; text-align: center; }
.pill { border-radius: 10px; font-size: 11px; font-weight: 700; padding: 1px 8px; }
.pill--alert { background: #fdecea; color: #e57373; }
.empty { text-align: center; padding: 32px 16px; color: #9aa0ba; }
.empty i { font-size: 32px; margin-bottom: 10px; opacity: .5; display: block; }
.empty p { font-size: 14px; font-weight: 500; color: #6b7a99; margin: 0 0 4px; }
.empty small { font-size: 12px; }

/* ─── Modal base ─── */
.modal-backdrop { position: fixed; inset: 0; background: rgba(30,34,58,0.4); backdrop-filter: blur(4px); z-index: 9999; display: flex; align-items: center; justify-content: center; }
.modal { background: #ffffff; border-radius: 16px; width: 90%; max-width: 520px; box-shadow: 0 4px 16px rgba(44,51,73,0.1); overflow: hidden; display: flex; flex-direction: column; }
.modal__head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #e9ecf5; gap: 10px; }
.modal__head-label { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
.modal__head-icon { width: 40px; height: 40px; background: #eef0fc; color: #5c6bc0; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.modal__head-title { font-size: 15px; font-weight: 700; color: #2d3142; }
.modal__head-sub { font-size: 12px; color: #6b7a99; margin-top: 1px; }
.modal__body { padding: 18px 20px; }
.modal__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 20px; border-top: 1px solid #e9ecf5; background: #f4f6fb; }

/* ─── File chip / compose ─── */
.file-chip { display: flex; align-items: center; gap: 12px; background: #f4f6fb; border: 1px solid #e9ecf5; border-radius: 12px; padding: 10px 12px; margin-bottom: 14px; position: relative; }
.file-chip__thumb { width: 48px; height: 48px; border-radius: 8px; overflow: hidden; flex-shrink: 0; }
.file-chip__icon { width: 48px; height: 48px; border-radius: 8px; background: #eef0fc; color: #5c6bc0; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
.file-chip__info { flex: 1; min-width: 0; }
.file-chip__name { display: block; font-size: 13px; font-weight: 500; color: #2d3142; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.file-chip__size { font-size: 11px; color: #9aa0ba; }
.file-chip__remove { position: absolute; top: 8px; right: 8px; width: 22px; height: 22px; background: rgba(0,0,0,0.15); border: none; border-radius: 50%; color: white; font-size: 11px; cursor: pointer; transition: background .15s; }
.file-chip__remove:hover { background: rgba(0,0,0,0.3); }
.compose-box { border: 1.5px solid #e9ecf5; border-radius: 12px; background: #f4f6fb; display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; position: relative; transition: border-color .2s; }
.compose-box:focus-within { border-color: #5c6bc0; background: #ffffff; }
.compose-box__attach { position: relative; flex-shrink: 0; }
.attach-btn { width: 30px; height: 30px; border: none; background: transparent; color: #9aa0ba; border-radius: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; transition: color .15s, background .15s; }
.attach-btn:hover, .attach-btn.active { color: #5c6bc0; background: #eef0fc; }
.attach-menu { position: absolute; bottom: 38px; left: 0; background: #ffffff; border: 1px solid #e9ecf5; border-radius: 12px; padding: 6px; box-shadow: 0 4px 16px rgba(44,51,73,0.1); z-index: 10; min-width: 130px; }
.attach-menu__item { display: flex; align-items: center; gap: 8px; width: 100%; padding: 7px 10px; border: none; background: none; border-radius: 8px; cursor: pointer; font-size: 13px; color: #2d3142; transition: background .13s; }
.attach-menu__item:hover { background: #eef0fc; color: #5c6bc0; }
.compose-box__textarea { flex: 1; min-height: 72px; max-height: 160px; border: none; outline: none; resize: vertical; font-family: inherit; font-size: 13px; line-height: 1.5; color: #2d3142; background: transparent; }
.compose-box__textarea::placeholder { color: #9aa0ba; }
.compose-box__count { position: absolute; bottom: 8px; right: 10px; font-size: 11px; color: #9aa0ba; }

/* ─── Transitions ─── */
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.pop-enter-active, .pop-leave-active { transition: transform .18s ease, opacity .18s ease; transform-origin: bottom left; }
.pop-enter, .pop-leave-to { transform: scale(.92) translateY(4px); opacity: 0; }

/* ─── WhatsApp dialog specific ─── */
.modal__head-icon--whatsapp { background: #e7faf0; color: #25D366; }

.modal--whatsapp { max-width: 620px; max-height: 88vh; display: flex; flex-direction: column; }
.modal--whatsapp .modal__head, .modal--whatsapp .modal__foot { flex-shrink: 0; }

.modal__body--whatsapp { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; padding: 18px 20px; }
.modal__body--whatsapp::-webkit-scrollbar { width: 5px; }
.modal__body--whatsapp::-webkit-scrollbar-thumb { background: #e9ecf5; border-radius: 4px; }

/* Language toggle */
.lang-toggle { display: flex; gap: 4px; background: #f4f6fb; border-radius: 20px; padding: 3px; border: 1px solid #e9ecf5; flex-shrink: 0; }
.lang-btn { border: none; background: transparent; color: #6b7a99; font-size: 12px; font-weight: 600; padding: 4px 10px; border-radius: 16px; cursor: pointer; transition: background .15s, color .15s; white-space: nowrap; }
.lang-btn:hover { background: #eef0fc; color: #5c6bc0; }
.lang-btn--active { background: #ffffff; color: #25D366; box-shadow: 0 1px 4px rgba(44,51,73,0.1); }

/* Selected client */
.wa-selected { display: flex; align-items: center; gap: 12px; background: linear-gradient(135deg,#e9fbf0 0%,#f0fdf5 100%); border: 1px solid #c8f0d8; border-radius: 14px; padding: 10px 12px; }
.wa-selected__avatar { position: relative; flex-shrink: 0; }
.wa-selected__avatar img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; display: block; }
.wa-selected__info { flex: 1; min-width: 0; }
.wa-selected__name { font-size: 13px; font-weight: 700; color: #1f2937; }
.wa-selected__phone { font-size: 12px; color: #2f9e5e; display: flex; align-items: center; gap: 5px; margin-top: 2px; }
.wa-selected__phone--missing { color: #d99a3f; }
.wa-selected__clear { width: 26px; height: 26px; border: none; border-radius: 50%; background: rgba(37,211,102,0.12); color: #25D366; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; transition: background .15s, transform .15s; flex-shrink: 0; }
.wa-selected__clear:hover { background: rgba(37,211,102,0.22); transform: scale(1.08); }

/* Search */
.wa-search { border: 1px solid #e9ecf5; border-radius: 12px; padding: 8px 12px; flex-shrink: 0; }

/* Client list */
.wa-list { flex-shrink: 0; max-height: 210px; overflow-y: auto; -webkit-mask-image: linear-gradient(to bottom,transparent 0,#000 12px,#000 calc(100% - 12px),transparent 100%); mask-image: linear-gradient(to bottom,transparent 0,#000 12px,#000 calc(100% - 12px),transparent 100%); }
.wa-list::-webkit-scrollbar { width: 5px; }
.wa-list::-webkit-scrollbar-thumb { background: #e9ecf5; border-radius: 4px; }
.wa-list__items { display: flex; flex-direction: column; gap: 6px; padding: 10px 4px; }
.wa-client { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 12px; cursor: pointer; border: 1.5px solid transparent; background: #f9fafc; transition: background .15s, border-color .15s, transform .15s, box-shadow .15s; }
.wa-client:hover { background: #eef0fc; transform: translateY(-1px); box-shadow: 0 2px 6px rgba(44,51,73,0.06); }
.wa-client--active { background: #e9fbf0; border-color: #25D366; box-shadow: 0 2px 8px rgba(37,211,102,0.18); }
.wa-client__avatar { position: relative; flex-shrink: 0; }
.wa-client__avatar img { width: 34px; height: 34px; border-radius: 50%; object-fit: cover; display: block; }
.wa-client__body { flex: 1; min-width: 0; }
.wa-client__name { font-size: 13px; font-weight: 600; color: #2d3142; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wa-client__phone { font-size: 11px; color: #6b7a99; display: flex; align-items: center; gap: 4px; margin-top: 2px; }
.wa-client__phone--missing { color: #d99a3f; }
.wa-client__check { width: 22px; height: 22px; border-radius: 50%; background: #25D366; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; opacity: 0; transform: scale(.4); transition: opacity .18s, transform .18s; flex-shrink: 0; }
.wa-client--active .wa-client__check { opacity: 1; transform: scale(1); }

/* Quick templates */
.wa-templates { flex-shrink: 0; }
.wa-templates__label { font-size: 12px; font-weight: 600; color: #6b7a99; display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
.wa-templates__row { display: flex; flex-wrap: wrap; gap: 6px; }
.wa-templates__chip { border: 1px solid #e9ecf5; background: #f9fafc; color: #5c6bc0; font-size: 12px; font-weight: 500; padding: 5px 12px; border-radius: 16px; cursor: pointer; transition: background .15s, border-color .15s, transform .15s; }
.wa-templates__chip:hover { background: #eef0fc; border-color: #dde1f8; transform: translateY(-1px); }

/* Message textarea */
.wa-label { display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px; color: #2d3142; }
.wa-textarea { width: 100%; border: 1.5px solid #e9ecf5 !important; border-radius: 12px; padding: 10px 12px; background: #f9fafc; transition: border-color .2s, background .2s; box-sizing: border-box; resize: vertical; font-family: inherit; font-size: 13px; line-height: 1.6; color: #2d3142; min-height: 140px; }
.wa-textarea:focus { border-color: #5c6bc0 !important; background: #fff; outline: none; }
.wa-textarea-foot { display: flex; justify-content: space-between; align-items: center; margin-top: 6px; gap: 8px; }
.template-hint { font-size: 11px; color: #9aa0ba; }
.wa-char-count { font-size: 11px; color: #9aa0ba; flex-shrink: 0; }
.wa-char-count--warn { color: #e57373; font-weight: 600; }

/* Preview — styled like a real WhatsApp bubble */
.wa-preview { background: #e5ddd5; border-radius: 14px; padding: 12px; flex-shrink: 0; overflow: hidden; }
.wa-preview--empty { opacity: .65; }
.wa-preview__head { font-size: 12px; font-weight: 600; color: #6b7a99; display: flex; align-items: center; gap: 6px; margin-bottom: 10px; }
.wa-preview__head i { color: #25D366; }
.wa-preview__phone-frame { display: flex; justify-content: flex-end; }
.wa-preview__bubble-wrap { max-width: 90%; display: flex; flex-direction: column; align-items: flex-end; }
.wa-preview__bubble { background: #dcf8c6; border-radius: 10px 0 10px 10px; padding: 10px 13px; font-size: 13px; line-height: 1.6; color: #1f2937; white-space: pre-wrap; word-break: break-word; max-height: 180px; overflow-y: auto; box-shadow: 0 1px 2px rgba(0,0,0,0.1); }
.wa-preview__tick { font-size: 11px; color: #888; margin-top: 4px; padding-right: 2px; }

/* Send pulse */
.btn--whatsapp-ready { animation: wa-pulse 1.8s ease-in-out infinite; }
@keyframes wa-pulse {
  0%, 100% { box-shadow: 0 2px 8px rgba(37,211,102,0.3); }
  50%       { box-shadow: 0 2px 16px rgba(37,211,102,0.55); }
}

/* List transitions */
.wa-item-enter-active, .wa-item-leave-active { transition: all .2s ease; }
.wa-item-enter, .wa-item-leave-to { opacity: 0; transform: translateX(-6px); }
.wa-item-move { transition: transform .25s ease; }

/* Modal entrance */
.modal-pop-enter-active, .modal-pop-leave-active { transition: opacity .2s ease; }
.modal-pop-enter, .modal-pop-leave-to { opacity: 0; }
.modal-pop-enter-active .modal--whatsapp, .modal-pop-leave-active .modal--whatsapp { transition: transform .22s ease, opacity .22s ease; }
.modal-pop-enter .modal--whatsapp, .modal-pop-leave-to .modal--whatsapp { transform: scale(.95) translateY(8px); opacity: 0; }

.empty-small { text-align: center; padding: 24px; color: #9ca3af; font-size: 13px; }
.empty-small i { font-size: 22px; margin-bottom: 6px; display: block; opacity: .5; }

/* ─── Responsive ─── */
@media (max-width: 1100px) { .dash-grid { grid-template-columns: 1fr 1fr; } .dash-grid > .panel:last-child { grid-column: 1 / -1; max-height: 340px; } }
@media (max-width: 680px) { .dash-root { padding: 12px; } .dash-header__inner { flex-direction: column; align-items: flex-start; gap: 12px; } .dash-grid { grid-template-columns: 1fr; } .dash-grid > .panel { max-height: 320px; } .stats-strip { padding: 10px 14px; overflow-x: auto; } .stat-chip { flex-shrink: 0; } .modal { width: 95%; margin: 12px; } .modal--whatsapp { max-height: 92vh; } .wa-list { max-height: 170px; } }
@media (max-width: 768px) { .dash-header__actions { justify-content: flex-start; margin-top: 8px; } .btn--topup { margin-left: 0 !important; } }
@media (max-width: 560px) { .dash-header__inner { flex-direction: column; align-items: stretch; gap: 12px; } .dash-header__actions { justify-content: center; } .lang-toggle { margin-left: auto; } }
</style>
